import React from 'react';
import * as Yup from 'yup';
import * as Ramda from 'ramda';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import { Flexbox } from '../../../../../../../../../components/layout/Flexbox';
import CbButton from '../../../../../../../../../components/Buttons/CbButton';
import { Show } from '../../../../../../../../../components/Show';
import { CardContainer } from './shared/StyledCardContainer';
import { ControlledTextField } from './shared/common';

export const InsuringAgreementsModal = ({
  data: modalProps,
  close,
}: InsuringAgreementsModalProps) => {
  const scrollableRef = React.useRef<null | HTMLDivElement>(null);
  const formMethods = useForm<InsuringAgreementsFormValues>({
    defaultValues: {
      agreements: modalProps.agreements,
    },
    resolver: yupResolver(schema),
  });

  const fieldArrayMethods = useFieldArray({
    control: formMethods.control,
    name: 'agreements',
  });

  const handleAddAgreements = ({ scroll }: { scroll: boolean }) => {
    fieldArrayMethods.append({
      paragraph: '',
      date: '',
    });

    if (scroll) {
      setTimeout(
        () => scrollableRef.current?.scrollIntoView({ behavior: 'smooth' }),
        300
      );
    }
  };

  React.useEffect(() => {
    /* When component first mounts, check if at least one entity exists. If so, dont add a card by default */
    if (formMethods.getValues().agreements[0]?.date) {
      return;
    }

    handleAddAgreements({ scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, modalProps.onSubmit)
        )}
      >
        <DialogContent style={{ padding: '1rem 2.5rem' }}>
          <Show when={!fieldArrayMethods.fields.length}>
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="primary">
                No Insuring Agreements
              </Typography>
            </Box>
          </Show>

          {fieldArrayMethods.fields.map((field, index) => {
            return (
              <CardContainer key={field.id} elevation={3}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <ControlledTextField
                      label="Insuring Agreement Date"
                      type="date"
                      required
                      name={`agreements.${index}.date`}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ControlledTextField
                      minRows={4}
                      multiline
                      required
                      label="Insuring Agreements Description"
                      name={`agreements.${index}.paragraph`}
                    />
                  </Grid>
                </Grid>

                <Button
                  className="contrast-text"
                  style={{ position: 'absolute', top: 5, right: 5 }}
                  onClick={() => fieldArrayMethods.remove(index)}
                >
                  Remove
                </Button>
              </CardContainer>
            );
          })}
          <div ref={scrollableRef} />
        </DialogContent>

        <DialogActions>
          <Flexbox>
            <Button
              startIcon={<AddIcon style={{ pointerEvents: 'none' }} />}
              className="contrast-text"
              onClick={() => handleAddAgreements({ scroll: true })}
            >
              Add Agreement
            </Button>
            <Box>
              <CbButton onClick={close} styleName="cancel">
                Cancel
              </CbButton>
              <CbButton type="submit" styleName="ctaButton">
                Save
              </CbButton>
            </Box>
          </Flexbox>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

//types
type InsuringAgreementsModalProps = {
  data: {
    onSubmit: (formValues: InsuringAgreementsFormValues) => void;
    agreements: InsuringAgreement[];
  };
  close: () => void;
};

export type InsuringAgreementsFormValues = {
  agreements: InsuringAgreement[];
};

export type InsuringAgreement = {
  paragraph: string;
  date: string;
};

//validations
const schema = Yup.object().shape({
  agreements: Yup.array().of(
    Yup.object().shape({
      date: Yup.string().trim().label('Agreement date').required(),
      paragraph: Yup.string().trim().label('Agreement description').required(),
    })
  ),
});

export const InsuringAgreementsModalConfig = {
  InsuringAgreementsModal: {
    component: InsuringAgreementsModal,
    config: {
      title: 'Insuring Agreements',
      fullWidth: true,
      maxWidth: 'md',
    },
  },
};
